import { useParams } from "react-router";
import { MODELS, useMultiModel } from "../../../api";
import type { SmallTeamId } from "../../../api/smallTeam";
import NotFoundPage from "../../not-found/NotFoundPage.jsx";
import type { AnySparkBag } from "../sparkBag";
import {
  FullShowroomEntryWithData,
  type CommentSection,
} from "./FullShowroomEntryWithData";

/**
 * One team's showroom entry, in a Lernreise and in the public demo alike.
 *
 * The demo has no workshop behind its spark, so there is no one to talk to:
 * it reads the entry without its engagement and shows it without comments.
 */
const FullShowroomEntry = ({ bag }: { bag: AnySparkBag }) => {
  const { smallTeamId } = useParams<{ smallTeamId: SmallTeamId }>();
  const { workshop, spark, settings } = bag;
  // Always present in practice — this screen only mounts on a matched
  // `/showroom/:smallTeamId` route — but react-router types every param as
  // possibly absent regardless.
  const teamId = smallTeamId ?? ("" as SmallTeamId);

  const [me, showroomData, teamMembers] = useMultiModel([
    MODELS.ME,
    MODELS.SMALL_TEAM_SHOWROOM_ENTRY(teamId, Boolean(workshop)),
    MODELS.SMALL_TEAM_MEMBERS(teamId),
  ]);
  const entry = showroomData.entry;

  // Dependent on the entry id, so a conditional fetch in its own slot rather
  // than joining the batch above.
  const [unseenComments] = useMultiModel([
    workshop && entry
      ? MODELS.COMMENTS_UNSEEN({
          workshopId: workshop.id,
          showroomEntryId: entry.id,
        })
      : null,
  ]);

  // A team that never uploaded anything has no entry to show — a stale link,
  // not a state this screen renders.
  if (!entry) return <NotFoundPage />;

  // Commenting is a Lernreise thing, and one that can be switched off.
  const commentSection: CommentSection | null =
    workshop && settings.commentsEnabled
      ? {
          comments: showroomData.comments,
          myCommentReactions: showroomData.myCommentReactions,
          me,
          workshopId: workshop.id,
          unseenComments,
          myRoles: workshop.roles ?? [],
        }
      : null;

  return (
    <FullShowroomEntryWithData
      entry={entry}
      teamMembers={teamMembers}
      myReaction={showroomData.myReaction}
      commentSection={commentSection}
      sparkId={spark.id}
      backPath="../showroom"
    />
  );
};

export default FullShowroomEntry;
